/**
 * The geometry of the query's radius on the map: a circle around each beacon district that covers
 * the district and reaches the radius beyond it. Plain spherical math, in [lon, lat] degrees.
 */

import type { FeatureCollection, Geometry, Polygon, Position } from 'geojson';

/** Earth's mean radius in miles. */
const EARTH_RADIUS_MILES = 3958.8;

/** How many points a circle's ring has; enough to look round at any zoom. */
const CIRCLE_STEPS = 64;

/** A [west, south, east, north] box in degrees. */
export type Bounds = [number, number, number, number];

/** A district's circle: its center and the miles from it that cover the whole district. */
export interface DistrictExtent {
  center: [number, number];
  extentMiles: number;
}

const toRadians = (degrees: number) => (degrees * Math.PI) / 180;
const toDegrees = (radians: number) => (radians * 180) / Math.PI;

/** The great-circle distance between two [lon, lat] points, in miles. */
export function milesBetween(
  [lon1, lat1]: [number, number],
  [lon2, lat2]: [number, number],
): number {
  const dLat = toRadians(lat2 - lat1);
  const dLon = toRadians(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_RADIUS_MILES * Math.asin(Math.min(1, Math.sqrt(a)));
}

/** The box of a Polygon's or MultiPolygon's coordinates, grown to take in prev; prev for others. */
export function growBounds(geometry: Geometry, prev?: Bounds): Bounds | undefined {
  let rings: Position[][];
  if (geometry.type === 'Polygon') rings = geometry.coordinates;
  else if (geometry.type === 'MultiPolygon') rings = geometry.coordinates.flat();
  else return prev;
  let [west, south, east, north] = prev ?? [Infinity, Infinity, -Infinity, -Infinity];
  for (const ring of rings) {
    for (const [lon, lat] of ring) {
      west = Math.min(west, lon);
      east = Math.max(east, lon);
      south = Math.min(south, lat);
      north = Math.max(north, lat);
    }
  }
  return west <= east ? [west, south, east, north] : prev;
}

/** The circle that covers a box: its center and the miles to its farthest corner. */
export function extentOf([west, south, east, north]: Bounds): DistrictExtent {
  const center: [number, number] = [(west + east) / 2, (south + north) / 2];
  const extentMiles = Math.max(
    milesBetween(center, [west, south]),
    milesBetween(center, [east, north]),
    milesBetween(center, [west, north]),
    milesBetween(center, [east, south]),
  );
  return { center, extentMiles };
}

/** The point the given miles from start along bearing (radians from north). */
function destination([lon, lat]: [number, number], miles: number, bearing: number): Position {
  const d = miles / EARTH_RADIUS_MILES;
  const φ1 = toRadians(lat);
  const λ1 = toRadians(lon);
  const φ2 = Math.asin(Math.sin(φ1) * Math.cos(d) + Math.cos(φ1) * Math.sin(d) * Math.cos(bearing));
  const λ2 =
    λ1 +
    Math.atan2(
      Math.sin(bearing) * Math.sin(d) * Math.cos(φ1),
      Math.cos(d) - Math.sin(φ1) * Math.sin(φ2),
    );
  return [toDegrees(λ2), toDegrees(φ2)];
}

/** A circle of the given miles around center, as a closed polygon ring. */
export function circlePolygon(center: [number, number], miles: number): Polygon {
  const ring: Position[] = [];
  for (let i = 0; i < CIRCLE_STEPS; i++) {
    ring.push(destination(center, miles, (2 * Math.PI * i) / CIRCLE_STEPS));
  }
  ring.push(ring[0]);
  return { type: 'Polygon', coordinates: [ring] };
}

/** One circle per district, covering it and reaching radiusMiles beyond; none at radius 0. */
export function radiusCircles(districts: DistrictExtent[], radiusMiles: number): FeatureCollection {
  return {
    type: 'FeatureCollection',
    features:
      radiusMiles > 0
        ? districts.map((d) => ({
            type: 'Feature',
            properties: {},
            geometry: circlePolygon(d.center, d.extentMiles + radiusMiles),
          }))
        : [],
  };
}
